<script lang="ts">
  import { onDestroy, onMount } from 'svelte';

  import { AppState, ToastQueue } from '../etc/stores';
  import { Backend } from '../services/backend';
  import { Container } from '../services/container';
  import Page from '../components/misc/Page.svelte';
  import Spinner from '../components/misc/Spinner.svelte';
  import { Logger } from '../services/logger';
  import { _ } from 'svelte-i18n';
  import HeaderInternal from '../components/misc/HeaderInternal.svelte';
  import Card from '../components/misc/Card.svelte';
  import ChevronRight from '../components/icons/ChevronRight.svelte';
  import { formatMoney, getParamsVisualTest } from '../etc/utils';
  import { FreeCoinsCampaign } from '../services/free-coins-campaign';
  import { extractMessageFromError } from '../services/api';
  import Check from '../components/icons/Check.svelte';
  import Analytics from '../services/analytics';
  import { mockCoupons } from '../mocks/mockVisualTest';
  import Label from '../components/typography/Label.svelte';
  import Text from '../components/typography/Text.svelte';
  import { useFinishSignIn } from '../etc/hooks/useModalInternal';
  import { useLocation, useNavigate } from 'svelte-navigator';
  import { Auth } from '../services/auth';
  import { replaceVariables } from '../i18n';
  import CoinIcon from '../components/icons/CoinV2.svelte';
  import PaymentCodeItem from '../components/misc/PaymentCodeItem.svelte';
  import { fade } from 'svelte/transition';
  import flagKeys from '../etc/flagKeys';

  const auth = Container.get(Auth);
  const backend = Container.get(Backend);
  const logger = Container.get(Logger).context('PaymentCodes.svelte') as Logger;
  const freeCoinsCampaign = Container.get(
    FreeCoinsCampaign
  ) as FreeCoinsCampaign;

  const location = useLocation();
  const navigate = useNavigate();

  const { open, close } = useFinishSignIn();

  $: hasFreeCoinsCampaign = !!$AppState.freeCoinsCampaign.id;
  $: hasAlreadyClaimedCampaign = !!$AppState.freeCoinsCampaign.alreadyRedeemed;
  let coupons = [];
  let availableCoupons = [];
  let redeemedCoupons = [];
  let expiredCoupons = [];
  let loading = false;
  let freeCoinsLoading = false;

  let startAnimation = false;
  let handleNavigationBackPage;
  let timeoutAnimation;

  const navAnimation: boolean = $location.state?.navAnimation;
  $: expirableCoinsIsActive = $AppState.featureFlags.includes(flagKeys.EXPIRABLE_COINS);

  let category = 'my-payment-codes-state';

  const filterCouponsByStatus = (status: string) => (coupon: any) => coupon.status === status;

  const t = (text: string, values: any = {}) => {
    return replaceVariables($_(`payment_codes.${text}`), values);
  };

  const loadList = async () => {
    try {
      loading = true;
      if (getParamsVisualTest().enabled) {
        coupons = mockCoupons;
      } else {
        const response = await backend.getCoupons($AppState.business.id);
        coupons = response.coupons;
      }

      availableCoupons = coupons.filter(filterCouponsByStatus('active'));
      redeemedCoupons = coupons.filter(filterCouponsByStatus('redeemed'));
      expiredCoupons = coupons.filter(filterCouponsByStatus('expired'));
    } catch (e) {
      logger.error(e);
      coupons = [];
    } finally {
      loading = false;
    }
  };

  const claimFreeCoinsCampaign = async () => {
    try {
      freeCoinsLoading = true;

      await freeCoinsCampaign.claimCampaignCoupon({
        analyticsCategory: category
      });

      loadList();

      ToastQueue.add({
        title: t('free_coins_campaign.success_title'),
        message: t('free_coins_campaign.success_message'),
        type: 'success',
        duration: 5000
      });
    } catch (e) {
      logger.error(e);

      ToastQueue.add({
        title: $_('error.title'),
        message: extractMessageFromError(e),
        type: 'error',
        duration: 5000
      });
    } finally {
      freeCoinsLoading = false;
    }
  };

  const onBack = () => {
    close();
    if (navAnimation) {
      clearTimeout(timeoutAnimation);
      startAnimation = true;
      timeoutAnimation = setTimeout(() => {
        if (handleNavigationBackPage) handleNavigationBackPage();
        navigate(-1);
      }, 600);
    } else {
      navigate(-1);
    }
  };

  const handleExpiredCoupon = ({ detail: couponId }: CustomEvent<string>) => {
    const newAvailableCoupons = availableCoupons.map(coupon => {
      if (coupon.id === couponId) {
        return {
          ...coupon,
          status: 'expired',
        }
      }

      return coupon;
    });

    expiredCoupons = newAvailableCoupons.filter(filterCouponsByStatus('expired'));
    availableCoupons = newAvailableCoupons.filter(coupon => coupon.id !== couponId);
  }

  onMount(async () => {
    if (await auth.isSoftLogin()) {
      open();
    } else {
      loadList();
    }
    Analytics.logPageView('My Payment Codes', '/payment-codes');
  });

  onDestroy(() => {
    clearTimeout(timeoutAnimation);
  });
</script>

<HeaderInternal
  title={t('title')}
  {onBack}
  {navAnimation}
  {startAnimation}
  bind:handleNavigationBackPage
/>
<Page padding="0" hideCloseButton headerInternal>
  <div
    class="payment-codes-wrapper"
    class:--animation={startAnimation}
    in:fade={{ duration: navAnimation ? 200 : 0 }}
  >
    {#if hasFreeCoinsCampaign}
      <div class="free-coins-campaign-container">
        <Card
          disabled={hasAlreadyClaimedCampaign}
          on:click={() => claimFreeCoinsCampaign()}
        >
          <div class="free-coins-campaign-row">
            <div class="free-coins-campaign-container-icon">
              <CoinIcon size="24" color={$AppState.theme.colors.primary200} />
            </div>

            {#if !hasAlreadyClaimedCampaign}
              <div class="free-coins-campaign-notification-bullet" />
            {/if}
            <Label type="medium-regular" class="free-coins-campaign-row-text">
              {t(
                `free_coins_campaign.box.${
                  hasAlreadyClaimedCampaign ? 'claimed' : 'claim'
                }`,
                {
                  moneyAmount: formatMoney(
                    $AppState.business.location,
                    $AppState.freeCoinsCampaign.coinsReward
                  )
                }
              )}
            </Label>
          </div>
          {#if freeCoinsLoading}
            <div class="twism-loading">
              <Spinner color="#6B6B6B" />
            </div>
          {:else if hasAlreadyClaimedCampaign}
            <Check />
          {:else}
            <ChevronRight color={$AppState.theme.colors.primary500} />
          {/if}
        </Card>
      </div>
    {/if}
    <div
      class="payment-codes-container"
      class:centered={loading || coupons.length === 0}
    >
      {#if loading}
        <div class="twism-loading">
          <Spinner color="#6B6B6B" />
        </div>
      {:else if coupons.length > 0}
        <div class="payment-codes-sections-container">
          {#if availableCoupons.length > 0}
            <Label
              component="h3"
              type="medium-regular"
              color="#7A89B0"
              class="payment-codes-list-header"
            >
              {t('list_available')}
            </Label>
            <div class="payment-codes-list">
              {#each availableCoupons as coupon}
                <div class="payment-codes__single">
                  <PaymentCodeItem data={coupon} on:expired={handleExpiredCoupon} />
                </div>
              {/each}
            </div>
          {/if}
          {#if redeemedCoupons.length > 0}
            <Label
              component="h3"
              type="medium-regular"
              color="#7A89B0"
              class="payment-codes-list-header"
            >
              {t('list_redeemed')}
            </Label>
            <div class="payment-codes-list">
              {#each redeemedCoupons as coupon}
                <div class="payment-codes__single">
                  <PaymentCodeItem
                    data={coupon}
                    disabled={coupon.status !== 'active'}
                  />
                </div>
              {/each}
            </div>
          {/if}
          {#if expirableCoinsIsActive && expiredCoupons.length > 0}
            <Label
              component="h3"
              type="medium-regular"
              color="#7A89B0"
              class="payment-codes-list-header"
            >
              {t('list_expired')}
            </Label>
            <div class="payment-codes-list">
              {#each expiredCoupons as coupon}
                <div class="payment-codes__single">
                  <PaymentCodeItem
                    data={coupon}
                    disabled={coupon.status !== 'active'}
                  />
                </div>
              {/each}
            </div>
          {/if}
        </div>
      {:else}
        <div class="payment-codes-container__empty">
          <Text type="medium" color="#949DB6" textAlign="center">
            {t('empty')} <br />
            <strong>{$AppState.business.name}</strong>
          </Text>
        </div>
      {/if}
    </div>
  </div>
</Page>

<style lang="scss">
  .payment-codes-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    flex: 1;

    transition: opacity 0.3s ease-in-out;
    &.--animation {
      opacity: 0;
    }
  }
  .payment-codes-container {
    overflow: auto;
    max-height: 448px;
    box-sizing: content-box;
    padding-top: 32px;
    display: flex;
    width: 100%;
    flex: 1;

    &.centered {
      align-items: center;
      justify-content: center;
      padding-top: 0;
    }

    &__empty {
      padding: 0 40px;
    }
  }
  .payment-codes {
    &__single {
      padding: 24px 0;
    }
  }

  .free-coins-campaign {
    &-container {
      width: 100%;
      box-sizing: border-box;
      padding: 32px 24px 0;
    }

    &-notification-bullet {
      width: 8px;
      height: 8px;
      border: 2px solid white;
      box-sizing: content-box;
      border-radius: 50%;
      background-color: var(--twism-theme-secondary);
      position: absolute;
      left: 24px;
      top: -3px;
    }

    &-container-icon {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--twism-theme-primary600);
      border-radius: 8px;
    }
    &-row {
      display: flex;
      align-items: center;
      width: 100%;
      position: relative;

      :global(.free-coins-campaign-row-text) {
        font-weight: 600;
        font-size: 14px;
        line-height: 16px;
        margin-left: 12px;
        width: 212px;
        padding: 0;

        @media screen and (max-width: 468px) {
          width: auto;
          max-width: 70%;
        }
      }
    }
  }

  .twism-loading {
    display: flex;
    justify-content: center;
  }

  .payment-codes-sections-container {
    width: 100%;
    padding: 0 24px;
  }

  .payment-codes-list {
    padding-bottom: 40px;
    & > div:not(:last-child) {
      border-bottom: 1px solid #f1f1f1;
    }

    & > div:first-child {
      padding-top: 16px;
    }
  }

  :global(.payment-codes-list-header) {
    font-weight: 500 !important;
  }

  @media screen and (max-width: 468px) {
    .payment-codes-container {
      height: 100vh;
      max-height: none;
      justify-content: center;
    }
  }
</style>
